import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useSeo } from "../lib/seo";

const ease = [0.16, 1, 0.3, 1];

export default function InsightDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/insights/${slug}?lang=${lang}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug, lang]);

  useSeo({
    title: post ? `${post.title} · OMNIA Insights` : "OMNIA Insights",
    description: post?.excerpt,
    image: post?.cover,
    type: "article",
    jsonLd: post && {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.cover,
      datePublished: post.date,
      articleSection: post.category,
      inLanguage: lang,
      author: { "@type": "Organization", name: "OMNIA Intelligence Group" },
      publisher: { "@type": "Organization", name: "OMNIA" },
    },
  });

  return (
    <div className="bg-cream text-navy overflow-x-hidden">
      <Navbar />
      <article className="pt-36 pb-16 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> {t("insights.back")}
          </Link>

          {notFound ? (
            <p className="text-navy/50">404 — Insight no encontrado.</p>
          ) : !post ? (
            <p className="text-navy/40">Cargando…</p>
          ) : (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
              <div className="flex items-center gap-3 overline text-navy/40 mb-6">
                <span>{post.category}</span><span className="w-1 h-1 rounded-full bg-navy/30" />
                <span>{post.read_time} {t("insights.minRead")}</span>
              </div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-[0.98]">{post.title}</h1>
              <p className="mt-6 text-xl text-navy/60 leading-relaxed">{post.excerpt}</p>
            </motion.div>
          )}
        </div>
      </article>

      {post && (
        <>
          <div className="max-w-5xl mx-auto px-6 lg:px-10 mb-14">
            <div className="aspect-[21/9] overflow-hidden bg-navy rounded-lg">
              <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-6 lg:px-10 space-y-6">
            {post.body.map((para, i) => (
              <p key={i} className="text-lg leading-[1.8] text-navy/80">{para}</p>
            ))}
          </div>

          {/* CTA */}
          <div className="max-w-3xl mx-auto px-6 lg:px-10 my-20">
            <div className="bg-navy text-cream grain rounded-lg p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <p className="font-editorial text-3xl leading-snug max-w-sm">{t("insights.cta")}</p>
              <Link to="/login" data-testid="insight-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-7 py-3.5 hover:opacity-90 transition-opacity whitespace-nowrap">
                {t("nav.enter")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Related */}
          <section className="pb-28 px-6 lg:px-10">
            <div className="max-w-7xl mx-auto">
              <div className="overline text-navy/40 mb-8">{t("insights.related")}</div>
              <div className="grid md:grid-cols-3 gap-6">
                {post.related.map((p) => (
                  <Link key={p.slug} to={`/insights/${p.slug}`} data-testid={`related-${p.slug}`}
                    className="group block bg-white border border-navy/10 hover:border-navy/25 hover:-translate-y-1 transition-all duration-500 overflow-hidden">
                    <div className="aspect-[16/10] overflow-hidden bg-navy">
                      <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-6">
                      <div className="overline text-navy/40">{p.category}</div>
                      <h3 className="mt-2 font-editorial text-xl leading-tight tracking-tight">{p.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
      <Footer />
    </div>
  );
}
